"use client"

import { ExternalLink, Download, Mail, Phone } from "lucide-react"
import type { ChatbotMessageUI, ChatbotAction } from "@/lib/chatbot/types"
import { cn } from "@/lib/utils"

// Lightweight markdown: bold, inline code, code blocks, links, lists
function renderMarkdown(text: string) {
  const lines = text.split("\n")
  const elements: React.ReactNode[] = []
  let inCodeBlock = false
  let codeBlockContent = ""
  let codeBlockLang = ""
  let tableHeaders: string[] | null = null

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // Code block toggle
    if (line.trim().startsWith("```")) {
      if (inCodeBlock) {
        elements.push(
          <pre
            key={`code-${i}`}
            className="my-2 overflow-x-auto rounded-md bg-zinc-900 p-3 text-xs text-zinc-100"
          >
            <code>{codeBlockContent.trimEnd()}</code>
          </pre>
        )
        codeBlockContent = ""
        codeBlockLang = ""
        inCodeBlock = false
      } else {
        inCodeBlock = true
        codeBlockLang = line.trim().slice(3)
      }
      continue
    }

    if (inCodeBlock) {
      codeBlockContent += (codeBlockContent ? "\n" : "") + line
      continue
    }

    // Empty line — skip aggressively to keep responses tight
    if (!line.trim()) {
      // Find the next non-empty line
      let nextNonEmpty = ""
      for (let j = i + 1; j < lines.length; j++) {
        if (lines[j].trim()) {
          nextNonEmpty = lines[j].trim()
          break
        }
      }
      const nextIsList = /^[-*]\s/.test(nextNonEmpty) || /^\d+\.\s/.test(nextNonEmpty)
      const nextIsHeading = /^#{1,6}\s/.test(nextNonEmpty)
      const nextIsTable = nextNonEmpty.startsWith("|")
      const nextIsRule = /^-{3,}$/.test(nextNonEmpty) || /^\*{3,}$/.test(nextNonEmpty)
      // Skip blank before list items, headings, tables, rules
      if (nextIsList || nextIsHeading || nextIsTable || nextIsRule) continue
      // Skip if previous element is already a break or nothing rendered yet
      const prevElement = elements[elements.length - 1]
      const prevIsBr =
        prevElement && typeof prevElement === "object" && (prevElement as any).type === "br"
      if (prevIsBr || elements.length === 0) continue
      elements.push(<br key={`br-${i}`} />)
      continue
    }

    // Horizontal rule — skip (renders as empty space otherwise)
    if (/^-{3,}$/.test(line.trim()) || /^\*{3,}$/.test(line.trim())) {
      continue
    }

    // Headings (all levels: #, ##, ###, etc.)
    const headingMatch = line.match(/^(#{1,6})\s+(.*)/)
    if (headingMatch) {
      elements.push(
        <p key={`h-${i}`} className="mt-2 mb-1 text-sm font-semibold">
          {formatInline(headingMatch[2])}
        </p>
      )
      continue
    }

    // Table rows — render as key-value pairs so nothing gets truncated
    if (line.includes("|") && line.trim().startsWith("|")) {
      const cells = line
        .split("|")
        .filter((c) => c.trim())
        .map((c) => c.trim())
      // Skip separator rows
      if (cells.every((c) => /^[-:]+$/.test(c))) continue
      // Skip header rows (store them for labeling)
      if (!tableHeaders) {
        tableHeaders = cells
        continue
      }
      // Render as a compact row: "Key — Value" for 2-col, or "Col1 · Col2 · Col3" for multi
      if (cells.length === 2) {
        elements.push(
          <div key={`tr-${i}`} className="text-xs leading-relaxed">
            <span className="font-medium">{formatInline(cells[0])}</span>
            <span className="text-muted-foreground"> — </span>
            <span>{formatInline(cells[1])}</span>
          </div>
        )
      } else {
        elements.push(
          <div key={`tr-${i}`} className="text-xs leading-relaxed">
            {cells.map((cell, ci) => (
              <span key={ci}>
                {ci === 0 ? (
                  <span className="font-medium">{formatInline(cell)}</span>
                ) : (
                  <span className="text-muted-foreground"> · {formatInline(cell)}</span>
                )}
              </span>
            ))}
          </div>
        )
      }
      continue
    }

    // Reset table headers when we leave a table
    if (tableHeaders) tableHeaders = null

    // List items
    if (/^[-*]\s/.test(line.trim())) {
      elements.push(
        <div key={`li-${i}`} className="flex gap-1.5 text-sm">
          <span className="mt-0.5 shrink-0">•</span>
          <span>{formatInline(line.trim().slice(2))}</span>
        </div>
      )
      continue
    }

    // Numbered list items
    if (/^\d+\.\s/.test(line.trim())) {
      const match = line.trim().match(/^(\d+)\.\s(.*)/)
      if (match) {
        elements.push(
          <div key={`ol-${i}`} className="flex gap-1.5 text-sm">
            <span className="mt-0.5 shrink-0 text-muted-foreground">{match[1]}.</span>
            <span>{formatInline(match[2])}</span>
          </div>
        )
        continue
      }
    }

    // Regular paragraph
    elements.push(
      <p key={`p-${i}`} className="text-sm">
        {formatInline(line)}
      </p>
    )
  }

  // Close any unclosed code block
  if (inCodeBlock && codeBlockContent) {
    elements.push(
      <pre
        key="code-unclosed"
        className="my-2 overflow-x-auto rounded-md bg-zinc-900 p-3 text-xs text-zinc-100"
      >
        <code>{codeBlockContent.trimEnd()}</code>
      </pre>
    )
  }

  return elements
}

// Inline formatting: bold, inline code, links, images
function formatInline(text: string): React.ReactNode {
  const parts: React.ReactNode[] = []
  let remaining = text
  let key = 0

  while (remaining.length > 0) {
    // Bold
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/)
    // Inline code
    const codeMatch = remaining.match(/`([^`]+)`/)
    // Image (must check before link since syntax is similar)
    const imageMatch = remaining.match(/!\[([^\]]*)\]\(([^)]+)\)/)
    // Markdown links
    const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/)
    // Bare URLs (not already inside markdown syntax)
    const bareUrlMatch = remaining.match(/(?<!\]\(|!\[)https?:\/\/[^\s)>\]]+/)

    // Find earliest match
    const matches = [
      boldMatch ? { type: "bold", match: boldMatch } : null,
      codeMatch ? { type: "code", match: codeMatch } : null,
      imageMatch ? { type: "image", match: imageMatch } : null,
      linkMatch ? { type: "link", match: linkMatch } : null,
      bareUrlMatch ? { type: "bareUrl", match: bareUrlMatch } : null,
    ]
      .filter(Boolean)
      .sort((a, b) => (a!.match.index || 0) - (b!.match.index || 0))

    if (matches.length === 0) {
      parts.push(remaining)
      break
    }

    const earliest = matches[0]!
    const idx = earliest.match.index || 0

    // Text before match
    if (idx > 0) {
      parts.push(remaining.slice(0, idx))
    }

    if (earliest.type === "bold") {
      parts.push(
        <strong key={`b-${key++}`} className="font-semibold">
          {earliest.match[1]}
        </strong>
      )
    } else if (earliest.type === "code") {
      parts.push(
        <code key={`c-${key++}`} className="rounded bg-muted px-1 py-0.5 text-xs font-mono">
          {earliest.match[1]}
        </code>
      )
    } else if (earliest.type === "image") {
      parts.push(
        // Chatbot messages may embed arbitrary user/assistant image URLs — host and
        // dimensions are unknown at build time, so next/image's optimization pipeline
        // (which requires allowlisted remotePatterns + known w/h) doesn't apply here.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          key={`img-${key++}`}
          src={earliest.match[2]}
          alt={earliest.match[1] || ""}
          className="my-2 max-w-full rounded-lg border border-border"
          loading="lazy"
        />
      )
    } else if (earliest.type === "link") {
      parts.push(
        <a
          key={`a-${key++}`}
          href={earliest.match[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline underline-offset-2"
        >
          {earliest.match[1]}
        </a>
      )
    } else if (earliest.type === "bareUrl") {
      const url = earliest.match[0]
      parts.push(
        <a
          key={`u-${key++}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline underline-offset-2 break-all"
        >
          {url}
        </a>
      )
    }

    remaining = remaining.slice(idx + earliest.match[0].length)
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>
}

// Action button labels and icons
function getActionLabel(action: ChatbotAction): { label: string; icon: React.ReactNode } {
  switch (action.type) {
    case "NAVIGATE": {
      const pathLabels: Record<string, string> = {
        "/": "Home",
        "/help": "Help Center",
        "/settings": "Settings",
        "/pricing": "View Pricing",
        "/faq": "FAQ",
        "/blogs": "Blog",
        "/contact": "Contact",
        "/feedback": "Feedback",
        "/account": "Account",
        "/leetcode-problems": "LeetCode Problems",
        "/interview-types": "Interview Types",
        "/signin": "Sign In",
        "/signup": "Sign Up",
      }
      return {
        label: pathLabels[action.payload || ""] || action.payload || "Go",
        icon: <ExternalLink className="h-3 w-3" />,
      }
    }
    case "DOWNLOAD": {
      const platformLabels: Record<string, string> = {
        "mac-arm64": "Mac (Apple Silicon)",
        "mac-intel": "Mac (Intel)",
        windows: "Windows",
      }
      return {
        label: `Download for ${platformLabels[action.payload || ""] || action.payload}`,
        icon: <Download className="h-3 w-3" />,
      }
    }
    case "CONTACT_SUPPORT":
      return { label: "Contact Support", icon: <Mail className="h-3 w-3" /> }
    default:
      return { label: "Action", icon: null }
  }
}

interface ChatbotMessageProps {
  message: ChatbotMessageUI
  onAction?: (action: ChatbotAction) => void
}

export default function ChatbotMessage({ message, onAction }: ChatbotMessageProps) {
  const isUser = message.role === "user"
  const isBot = message.role === "assistant"

  return (
    <div className={cn("flex w-full", isUser ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[85%] rounded-2xl px-3.5 py-2.5",
          isUser
            ? "bg-primary text-primary-foreground rounded-br-md"
            : "bg-muted text-foreground rounded-bl-md"
        )}
      >
        {/* Message content */}
        <div className="space-y-1">
          {isUser ? <p className="text-sm">{message.content}</p> : renderMarkdown(message.content)}
          {/* Streaming cursor */}
          {message.isStreaming && (
            <span className="inline-block h-4 w-0.5 animate-pulse bg-foreground/60" />
          )}
        </div>

        {/* Contact support inline */}
        {isBot &&
          message.actions?.some((a) => a.type === "CONTACT_SUPPORT") &&
          !message.isStreaming && (
            <div className="mt-2 flex flex-col gap-1 rounded-lg bg-background/50 p-2 text-xs">
              <div className="flex items-center gap-1.5">
                <Mail className="h-3 w-3 text-muted-foreground" />
                <a
                  href="mailto:customer-care@phantomcodeai.com"
                  className="text-primary underline underline-offset-2"
                >
                  customer-care@phantomcodeai.com
                </a>
              </div>
              <div className="flex items-center gap-1.5">
                <Phone className="h-3 w-3 text-muted-foreground" />
                <a href="tel:+918168541082" className="text-primary underline underline-offset-2">
                  +91-8168541082
                </a>
              </div>
              <a
                href="/instant-support"
                className="mt-1 inline-flex items-center justify-center rounded-md bg-primary px-2.5 py-1.5 font-medium text-primary-foreground"
              >
                Raise a support request
              </a>
            </div>
          )}

        {/* Action buttons */}
        {isBot &&
          !message.isStreaming &&
          message.actions &&
          message.actions.filter((a) => a.type !== "CONTACT_SUPPORT").length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {message.actions
                .filter((a) => a.type !== "CONTACT_SUPPORT")
                .map((action, idx) => {
                  const { label, icon } = getActionLabel(action)
                  return (
                    <button
                      key={idx}
                      onClick={() => onAction?.(action)}
                      className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                    >
                      {icon}
                      {label}
                    </button>
                  )
                })}
            </div>
          )}
      </div>
    </div>
  )
}
